import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { useLocalization } from "@/hooks/useLocalization";

const MAIN_STORE = "https://kaspreadsheet.com";

export default function Hero() {
  const { t } = useTranslation();
  const { lang } = useLocalization();
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedCat, setSelectedCat] = useState("");

  const handleCategorySelect = (catId: number) => {
    const cat = categories.find((c) => c.id === catId);
    if (cat) {
      window.open(cat.url, "_blank", "noopener,nofollow");
    }
    setSearchOpen(false);
    setSelectedCat("");
  };

  return (
    <section className="relative w-full min-h-[700px] md:min-h-[800px] flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20fashion%20flat%20lay%20overhead%20photography%20of%20luxury%20streetwear%20clothing%20spread%20out%20on%20a%20soft%20beige%20linen%20background%20with%20sneakers%20hoodies%20t-shirts%20watches%20and%20accessories%20arranged%20artistically%20warm%20natural%20light%20editorial%20style%20muted%20earth%20tones%20no%20text&width=1600&height=900&seq=hero-bg-2026-01&orientation=landscape"
          alt="AllChinaBuy spreadsheet"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-5xl px-4 py-24 text-center md:px-6 md:py-32">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md px-4 py-1.5 mb-6">
          <span className="w-2 h-2 rounded-full bg-primary-400"></span>
          <span className="text-xs font-semibold tracking-wider uppercase text-white/90">
            {t("hero.badge")}
          </span>
        </div>

        <h1 className="font-heading text-4xl font-bold leading-tight text-white md:text-6xl lg:text-7xl">
          The Most Complete
          <br />
          <span className="text-accent-400">AllChinaBuy</span> Spreadsheet
          <br />
          Directory
        </h1>

        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/80 md:text-lg">
          {t("hero.copy")}
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={MAIN_STORE}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-foreground-950 transition-all hover:bg-white/90 whitespace-nowrap cursor-pointer"
          >
            AllChinaBuy Sheet
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </span>
          </a>
          <a
            href={MAIN_STORE}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-accent-500 px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-accent-600 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-sm"></i>
            </span>
          </a>
        </div>

        {/* Search dropdown */}
        <div className="mx-auto mt-10 w-full max-w-md relative">
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-full bg-white/10 backdrop-blur-md px-5 py-3 text-sm text-white/70 border border-white/15 hover:bg-white/15 transition-colors cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-white/60"></i>
              </span>
              {selectedCat
                ? categories.find((c) => c.id === selectedCat)?.name[lang] || t("dropdown.placeholder")
                : t("dropdown.placeholder")}
            </span>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={`${searchOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-white/60`}></i>
            </span>
          </button>

          {searchOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 rounded-2xl bg-white shadow-xl border border-background-200/70 overflow-hidden z-20">
              <div className="max-h-64 overflow-y-auto py-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleCategorySelect(cat.id)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-primary-600">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    <span className="font-medium">{cat.name[lang]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Meta info under search */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-white/50">
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-refresh-line"></i>
            </span>
            Updated Weekly
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-shield-check-line"></i>
            </span>
            Quality Checked
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-price-tag-3-line"></i>
            </span>
            14 Categories
          </span>
        </div>
      </div>
    </section>
  );
}